"use client";

import { useEffect, useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaDownload,
} from "react-icons/fa";
import { useSearchParams } from "next/navigation";
import { exportToCSVWithHeaders } from "@/lib/csvExport";

interface AntigenData {
  Antigen_Name: string;
  Antigen_Slug: string;
  Antigen_IRI: string | null;
  helper_tcell: number;
  cytotoxic_tcell: number;
  b_cell: number;
  total_epitopes: number;
  reference_value: string | null;
}

const AntigenPage = () => {
  const searchParams = useSearchParams();

  const [antigens, setAntigens] = useState<AntigenData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<keyof AntigenData>("Antigen_Name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const urlSearchTerm = searchParams.get("search");

    if (urlSearchTerm && urlSearchTerm.trim()) {
      setCurrentPage(1);      
      setSearchTerm(urlSearchTerm);
      fetchAntigen(urlSearchTerm);
    } else {
      fetchAntigen();
    }
  }, [searchParams]);

  useEffect(() => {
    if (searchTerm === "") {
      fetchAntigen();
    } else {
      fetchAntigen(searchTerm);
    }
  }, [searchTerm, currentPage, recordsPerPage]);

  const fetchAntigen = async (term = searchTerm) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: recordsPerPage.toString(),
      });
      if (term.trim()) params.set("search", term.trim());

      const response = await fetch(`/api/antigen-epitope-all?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch antigen list");

      const payload = await response.json();
      const records: AntigenData[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];

      setAntigens(records);
      const total = payload?.pagination?.total_count;
      setTotalRecords(typeof total === "number" ? total : records.length);
    } catch (error) {
      console.error("Error fetching antigen:", error);
      setAntigens([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: keyof AntigenData) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedAntigens = useMemo(() => {
    const data = [...antigens];
    data.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === "asc" ? 1 : -1;
      if (bVal == null) return sortDirection === "asc" ? -1 : 1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return sortDirection === "asc" ? -1 : 1;
      if (aStr > bStr) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return data;
  }, [antigens, sortDirection, sortField]);

  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage));

  const paginate = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10);
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: keyof AntigenData }) => {
    if (sortField !== field) return <FaSort className="inline ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="inline ml-1" />
    ) : (
      <FaSortDown className="inline ml-1" />
    );
  };

  const handleExportCSV = () => {
    const csvData = sortedAntigens.map(item => ({
      "Protein Name": item.Antigen_Name,
      "Helper T Cell": item.helper_tcell ?? 0,
      "Cytotoxic T Cell": item.cytotoxic_tcell ?? 0,
      "B Cell": item.b_cell ?? 0,
      "Total Epitopes": item.total_epitopes ?? 0,
      "References": item.reference_value || "-",
    }));
    
    exportToCSVWithHeaders(
      csvData,
      `antigen-data-${new Date().toISOString().split('T')[0]}`
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Head>
        <title>Antigen Database - ASFV-EpiBase</title>
        <meta
          name="description"
          content="Browse antigen data with T-cell and B-cell epitope information in ASFV-EpiBase"
        />
      </Head>

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Antigen Database</h1>
        <p className="text-gray-600">
          Browse antigen data with T-cell and B-cell epitope information
        </p>
      </div>

      {/* Search and Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 max-w-md">
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
            Search Antigen Name
          </label>
          <input
            id="search"
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Enter antigen name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm"
            title="Export to CSV"
          >
            <FaDownload className="w-4 h-4" />
            Export CSV
          </button>
          
          <div className="flex items-center gap-2">
            <label htmlFor="recordsPerPage" className="text-sm font-medium text-gray-700">
              Records per page:
            </label>
            <select
              id="recordsPerPage"
              value={recordsPerPage}
              onChange={handleRecordsPerPageChange}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse table-auto text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th
                className="px-2 py-2 text-left cursor-pointer"
                onClick={() => handleSort("Antigen_Name")}
              >
                Protein Name <SortIcon field="Antigen_Name" />
              </th>
              <th 
                className="px-2 py-2 text-left cursor-pointer"
                onClick={() => handleSort("helper_tcell")}
              >
                Helper T Cell <SortIcon field="helper_tcell" />
              </th>
              <th 
                className="px-2 py-2 text-left cursor-pointer"
                onClick={() => handleSort("cytotoxic_tcell")}
              >
                Cytotoxic T Cell <SortIcon field="cytotoxic_tcell" />
              </th>
              <th 
                className="px-2 py-2 text-left cursor-pointer"
                onClick={() => handleSort("b_cell")}
              >
                B Cell <SortIcon field="b_cell" />
              </th>
              <th 
                className="px-2 py-2 text-left cursor-pointer"
                onClick={() => handleSort("total_epitopes")}
              >
                Total Epitopes <SortIcon field="total_epitopes" />
              </th>
              <th 
                className="px-2 py-2 text-left cursor-pointer"
                onClick={() => handleSort("reference_value")}
              >
                References <SortIcon field="reference_value" />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAntigens.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4 text-gray-500">
                  {isLoading ? "Loading..." : "No records found"}
                </td>
              </tr>
            ) : (
              sortedAntigens.map((row, index) => (
                <tr key={`${row.Antigen_Name}-${index}`} className="border-b">
                  <td className="px-2 py-2">
                    <Link
                      href={`/antigen/${encodeURIComponent(row.Antigen_Slug || row.Antigen_Name)}`}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      {row.Antigen_Name || "-"}
                    </Link>
                  </td>
                  <td className="px-2 py-2">{row.helper_tcell ?? 0}</td>
                  <td className="px-2 py-2">{row.cytotoxic_tcell ?? 0}</td>
                  <td className="px-2 py-2">{row.b_cell ?? 0}</td>
                  <td className="px-2 py-2">{row.total_epitopes ?? 0}</td>
                  <td className="px-2 py-2">
                    {row.reference_value ? (
                      (() => {
                        // Split by comma or semicolon and clean up
                        const refs = row.reference_value
                          .split(/[,;]/)
                          .map(r => r.trim())
                          .filter(Boolean)
                          .filter(r => /^\d+$/.test(r)); // Only keep numeric PMIDs
                        
                        if (refs.length === 0) {
                          return <span className="text-gray-400">-</span>;
                        }
                        
                        return refs.map((pmid, idx) => (
                          <span key={idx}>
                            {idx > 0 && ", "}
                            <a
                              href={`https://pubmed.ncbi.nlm.nih.gov/${pmid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 hover:underline"
                              title={`View PMID ${pmid} on PubMed`}
                            >
                              {pmid}
                            </a>
                          </span>
                        ));
                      })()
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalRecords > 0 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-700">
            Showing {((currentPage - 1) * recordsPerPage) + 1} - {Math.min(currentPage * recordsPerPage, totalRecords)} of {totalRecords} records
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              title="First page"
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <FaAngleDoubleLeft />
            </button>
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              title="Previous page"
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <FaAngleLeft />
            </button>
            
            <span className="px-3 py-1 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              title="Next page"
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <FaAngleRight />
            </button>
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
              className="px-3 py-1 text-sm border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <FaAngleDoubleRight />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AntigenPage;
